import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  computed,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { MessageService, ConfirmationService } from 'primeng/api';
import {
  DireccionPersona,
  DireccionPersonaRequest,
  createEmptyDireccionRequest
} from 'src/app/domain/model/persona/direccion-persona';
import { DireccionPersonaRepository } from 'src/app/domain/service/persona/direccion-persona-repository';
import { environment } from 'src/environments/environment';
import { FormateadorDireccionComponent } from './formateador-direccion.component';
import { BuscadorMunicipioComponent } from '../shared/buscadores/buscador-municipio.component';
import { Municipio } from 'src/app/domain/model/municipio/municipio';
import { MunicipioRepository } from 'src/app/domain/service/municipio/municipio-repository';
import { NomenclaturaRepository } from 'src/app/domain/service/nomenclatura/nomenclatura-repository';
import {
  NomenclaturaDto,
  TipoDireccionDto
} from 'src/app/domain/shared/models/nomenclatura';
import { phoneKeypress, sanitizePhone } from 'src/app/common/input-utils';
import { direccionesPersonaPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * ===============================================================================
 * MIGRACION COMPLETADA: Constantes -> NomenclaturaService dinamico
 * ===============================================================================
 *
 * ANTES - Constantes hardcoded:
 * const TIPOS_DIRECCION_LEGACY_EJEMPLO = [...];
 * const TIPOS_VIA = [...];
 * const APENDICES = [...];
 * const CARDINALIDADES = [...];
 *
 * AHORA - Carga dinamica desde backend:
 * - nomenclaturaService.listarTiposDireccionPorPersona(cdPersona)
 * - nomenclaturaService.listarTiposVia()
 * - nomenclaturaService.listarApendices()  
 * - nomenclaturaService.listarCardinalidades()
 *
 * BACKEND ENDPOINTS:
 * GET /api/nomenclatura/tipos-direccion/{cdPersona}  (con logica FilterType)
 * GET /api/nomenclatura/tipos-via                   (desde TPSC_PARAM_NOMENCLATURA)
 * GET /api/nomenclatura/apendices                   (desde TPSC_PARAM_NOMENCLATURA)
 * GET /api/nomenclatura/cardinalidades              (desde TPSC_PARAM_NOMENCLATURA)
 *
 * LEGACY COMPATIBILITY:
 * AdminDireccionesActivity.listarTiposDirecciones() -> REST API
 * UtilidadesBuscadores.llenarListBox()              -> NomenclaturaService
 * SuraCommand "recuperarDatosLista"                 -> HTTP GET requests
 * ===============================================================================
 */

/**
 * ===============================================================================
 * MIGRACION: 100% dinamica sin hardcoded data
 * ===============================================================================
 *
 * ESTRATEGIA IMPLEMENTADA:
 * - Nomenclaturas completamente dinamicas desde backend
 * - Signals vacios cuando backend no disponible  
 * - UI degradada elegantemente sin datos hardcoded
 * - Usuario puede continuar con campos opcionales vacios
 *
 * VENTAJAS:
 * - Cero datos hardcoded a mantener
 * - Transicion automatica cuando backend este listo
 * - Estado consistente: loading -> datos OR vacio
 * - UX clara: "No disponible" vs "Seleccione..."
 *
 * MANEJO DE ESTADOS:
 * - loadingNomenclatura.set(true) -> "Cargando..."
 * - Backend disponible -> Datos reales en dropdowns
 * - Backend NO disponible -> Arrays vacios + placeholders informativos
 * - Formulario funcional con campos obligatorios minimos
 *
 * CAMPOS:
 * - Obligatorios: tipo direccion, municipio, direccion
 * - Opcionales: estrato, apendices, cardinalidades, coordenadas
 * ===============================================================================
 */

/**
 * Componente para gestion de direcciones de una persona
 * Se usa dentro del dialogo de edicion de personas
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-direcciones-persona',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    InputNumberModule,
    DropdownModule,
    TooltipModule,
    ConfirmDialogModule,
    TagModule,
    FormateadorDireccionComponent,
    BuscadorMunicipioComponent,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <div class="direcciones-container">
      <!-- Toolbar -->
      <div
        class="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3"
      >
        <span class="text-sm text-gray-600">
          {{ direcciones().length }} direcciones registradas
        </span>
        <button
          pButton
          label="Nueva Dirección"
          icon="pi pi-plus"
          class="p-button-sm"
          [disabled]="!cdpersona"
          (click)="openNew()"
        ></button>
      </div>

      <!-- Tabla de direcciones -->
      <div class="md:hidden space-y-3 p-3">
        @for (dir of direcciones(); track dir.nmconsecutivo ?? $index) {
          <div
            class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
          >
            <div class="mb-3 min-w-0">
              <div class="text-xs text-gray-500 dark:text-gray-400">
                Dirección
              </div>
              <div
                class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                [attr.title]="dir.dsdireccion"
              >
                {{ dir.dsdireccion }}
              </div>
            </div>
            <div class="grid grid-cols-1 gap-2 text-sm mb-3">
              <div>
                <span class="text-gray-500 dark:text-gray-400">Tipo:</span>
                <span class="text-gray-800 dark:text-gray-200">{{
                  getTipoLabel(dir.nmtipo_persona_direccion)
                }}</span>
              </div>
              <div>
                <span class="text-gray-500 dark:text-gray-400">Municipio:</span>
                <span class="text-gray-800 dark:text-gray-200">{{
                  dir.dsmunicipio || dir.cdmunicipio
                }}</span>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-2">
              <button
                pButton
                icon="pi pi-phone"
                label="Teléfonos"
                class="p-button-outlined p-button-sm min-h-[44px]"
                (click)="verTelefonos(dir)"
              ></button>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  pButton
                  icon="pi pi-pencil"
                  label="Modificar"
                  class="p-button-outlined p-button-sm min-h-[44px]"
                  (click)="editDireccion(dir)"
                ></button>
                <button
                  pButton
                  icon="pi pi-trash"
                  label="Eliminar"
                  class="p-button-danger p-button-outlined p-button-sm min-h-[44px]"
                  [disabled]="dir.snactivo === 'N'"
                  (click)="confirmDelete(dir)"
                ></button>
              </div>
            </div>
          </div>
        }
      </div>

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="direccionesPaginadas()"
          [loading]="loading()"
          [paginator]="false"
          [rows]="pageSize()"
          [scrollable]="true"
          [responsiveLayout]="'scroll'"
          [tableStyle]="{
            'min-width': tableConfig().responsive.minWidth || '72rem'
          }"
          [scrollHeight]="tableConfig().responsive.scrollHeight || '420px'"
          [styleClass]="tableStyleClass()"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn style="width: 120px">
                <span class="grid-header-text" title="Tipo">Tipo</span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Dirección"
                  >Dirección</span
                >
              </th>
              <th pResizableColumn style="width: 130px">
                <span class="grid-header-text" title="Nombre Municipio"
                  >Nombre Municipio</span
                >
              </th>
              <th pResizableColumn style="width: 100px">
                <span class="grid-header-text" title="Teléfonos"
                  >Teléfonos</span
                >
              </th>
              <th pResizableColumn style="width: 120px">
                <span class="grid-header-text" title="Acciones">Acciones</span>
              </th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-dir>
            <tr>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="
                    textoTabla(getTipoLabel(dir.nmtipo_persona_direccion))
                  "
                  >{{
                    textoTabla(getTipoLabel(dir.nmtipo_persona_direccion))
                  }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [pTooltip]="dir.dsdireccion"
                  [attr.title]="textoTabla(dir.dsdireccion)"
                  >{{ textoTabla(dir.dsdireccion) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="textoTabla(dir.dsmunicipio || dir.cdmunicipio)"
                  >{{ textoTabla(dir.dsmunicipio || dir.cdmunicipio) }}</span
                >
              </td>
              <td class="text-center">
                <button
                  pButton
                  icon="pi pi-phone"
                  label="Teléfonos"
                  class="p-button-outlined p-button-sm"
                  pTooltip="Ver teléfonos"
                  (click)="verTelefonos(dir)"
                ></button>
              </td>
              <td class="text-center" style="white-space: nowrap;">
                <button
                  pButton
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-text p-button-sm mr-1 min-w-[44px] min-h-[44px]"
                  pTooltip="Modificar"
                  (click)="editDireccion(dir)"
                ></button>
                <button
                  pButton
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                  pTooltip="Eliminar"
                  [disabled]="dir.snactivo === 'N'"
                  (click)="confirmDelete(dir)"
                ></button>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="5" class="text-center p-4 text-gray-500">
                <i class="pi pi-map-marker text-3xl mb-2 block"></i>
                No hay direcciones registradas
              </td>
            </tr>
          </ng-template>
        </p-table>

        @if (totalRegistros() > 0) {
          <div class="text-sm text-gray-500 px-3 pt-2">
            Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
            {{ totalRegistros() }}
          </div>
          <app-busqueda-paginador
            [currentPage]="currentPage()"
            [totalPages]="totalPages()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="pageSize()"
            (pageSizeChange)="onPageSizeChange($event)"
            (first)="irPrimeraPagina()"
            (previous)="irPaginaAnterior()"
            (next)="irPaginaSiguiente()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        }
      </div>

      <!-- Dialog Crear/Editar Dirección Básica -->
      <p-dialog
        [(visible)]="dialogVisible"
        [header]="editMode() ? 'Editar Dirección' : 'Nueva Dirección'"
        [modal]="true"
        [style]="{ width: '92vw', maxWidth: '30rem' }"
        [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
        [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
        [closable]="true"
        [draggable]="false"
        appendTo="body"
      >
        <!-- Formulario simplificado segun solicitud -->
        <div class="space-y-4">
          <!-- Tipo Dirección * -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Tipo *
            </label>
            <p-dropdown
              [(ngModel)]="direccionForm.nmtipo_persona_direccion"
              [options]="tiposDireccionOptions()"
              optionLabel="label"
              optionValue="value"
              placeholder="--Seleccione una opción--"
              [showClear]="true"
              [filter]="true"
              filterBy="label"
              appendTo="body"
              [loading]="loadingNomenclatura()"
              (onChange)="onTipoChange($event)"
              [style]="{ width: '100%' }"
            >
            </p-dropdown>
          </div>

          <!-- Dirección * -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Dirección *
            </label>
            <div class="flex gap-2 min-w-0">
              <input
                pInputText
                [(ngModel)]="direccionForm.dsdireccion"
                placeholder="Ingrese la dirección"
                maxlength="200"
                class="flex-1 min-w-0"
              />
              <button
                pButton
                label="Ingresar Dirección"
                icon="pi pi-cog"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Formatear Dirección Colombia"
                (click)="abrirFormateadorDireccion()"
              ></button>
            </div>
          </div>

          <!-- Nombre Municipio * -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Nombre Municipio *
            </label>
            <div class="flex gap-2 min-w-0">
              <input
                pInputText
                [value]="
                  municipioSeleccionado()?.dsmunicipio ||
                  direccionForm.cdmunicipio
                "
                placeholder="Seleccionar municipio..."
                class="flex-1 min-w-0"
                readonly
              />
              <button
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                pTooltip="Buscar Municipio"
                (click)="abrirBuscadorMunicipio()"
              ></button>
            </div>
            @if (municipioSeleccionado()) {
              <small class="text-gray-500"
                >Código: {{ municipioSeleccionado()?.cdmunicipio }}</small
              >
            }
          </div>

          <!-- Teléfono Fijo -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Teléfono Fijo
            </label>
            <input
              pInputText
              [(ngModel)]="direccionForm.dstelefono_fijo"
              placeholder="Ej: 6041234567"
              maxlength="20"
              (keypress)="phoneKeypress($event)"
              (change)="onPhoneInput($event, 'dstelefono_fijo')"
              class="w-full"
            />
          </div>

          <!-- Teléfono Celular -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Teléfono Celular
            </label>
            <input
              pInputText
              [(ngModel)]="direccionForm.dstelefono_celular"
              placeholder="Ej: 3001234567"
              maxlength="20"
              (keypress)="phoneKeypress($event)"
              (change)="onPhoneInput($event, 'dstelefono_celular')"
              class="w-full"
            />
          </div>

          <!-- Fax -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Fax
            </label>
            <input
              pInputText
              [(ngModel)]="direccionForm.dsfax"
              placeholder="Número de fax"
              maxlength="20"
              (keypress)="phoneKeypress($event)"
              (change)="onPhoneInput($event, 'dsfax')"
              class="w-full"
            />
          </div>
        </div>

        <ng-template pTemplate="footer">
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            <p-button
              label="Cerrar"
              icon="pi pi-times"
              [outlined]="true"
              (onClick)="hideDialog()"
            >
            </p-button>
            <p-button
              label="Guardar"
              icon="pi pi-check"
              [loading]="saving()"
              (onClick)="saveDireccion()"
            >
            </p-button>
          </div>
        </ng-template>
      </p-dialog>

      <!-- Confirm Dialog para eliminar -->
      <p-confirmDialog key="direccionConfirm"></p-confirmDialog>

      <!-- Modal Buscador de Municipio -->
      <app-buscador-municipio
        (municipioSeleccionado)="onMunicipioSeleccionado($event)"
      >
      </app-buscador-municipio>

      <!-- Modal Formateador de Dirección -->
      <app-formateador-direccion
        [tiposVia]="tiposVia()"
        [apendices]="apendices()"
        [cardinalidades]="cardinalidades()"
        (direccionFormateada)="onDireccionFormateada($event)"
      >
      </app-formateador-direccion>

      <!-- Modal Teléfonos -->
      <p-dialog
        [(visible)]="telefonosDialogVisible"
        header="Teléfonos"
        [modal]="true"
        [style]="{ width: '92vw', maxWidth: '24rem' }"
        [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
        [breakpoints]="{ '640px': '96vw' }"
        [closable]="true"
        [draggable]="false"
        appendTo="body"
      >
        <div class="space-y-4">
          <div class="field">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Teléfono Fijo</label
            >
            <div class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ telefonosDireccion.fijo || 'No registrado' }}
            </div>
          </div>
          <div class="field">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Teléfono Celular</label
            >
            <div class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ telefonosDireccion.celular || 'No registrado' }}
            </div>
          </div>
          <div class="field">
            <label
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Fax</label
            >
            <div class="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {{ telefonosDireccion.fax || 'No registrado' }}
            </div>
          </div>
        </div>

        <ng-template pTemplate="footer">
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            <p-button
              label="Cerrar"
              icon="pi pi-times"
              [outlined]="true"
              (onClick)="telefonosDialogVisible = false"
            >
            </p-button>
          </div>
        </ng-template>
      </p-dialog>
    </div>
  `
})
export class DireccionesPersonaComponent implements OnInit, OnChanges {
  @Input() cdpersona?: number;
  @Input() visible = false; // Para forzar recarga cuando el dialogo padre se abre

  @ViewChild(BuscadorMunicipioComponent)
  buscadorMunicipio!: BuscadorMunicipioComponent;
  @ViewChild(FormateadorDireccionComponent)
  formateadorDireccion!: FormateadorDireccionComponent;

  // Inyectando PUERTOS (no adaptadores) - Arquitectura Hexagonal
  private readonly direccionRepository = inject(DireccionPersonaRepository);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly municipioRepository = inject(MunicipioRepository);
  private readonly nomenclaturaService = inject(NomenclaturaRepository);

  // Estado
  direcciones = signal<DireccionPersona[]>([]);
  loading = signal(false);
  saving = signal(false);
  editMode = signal(false);
  loadingNomenclatura = signal(false);
  dialogVisible = false;

  // Paginación manual (frontend-girop §17 / ERROR 6 - prohibido [paginator]="true")
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly totalRegistros = computed(() => this.direcciones().length);
  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.totalRegistros() / this.pageSize()))
  );
  readonly direccionesPaginadas = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.direcciones().slice(start, start + this.pageSize());
  });
  readonly hasPrevious = computed(() => this.currentPage() > 1);
  readonly hasNext = computed(() => this.currentPage() < this.totalPages());
  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });
  readonly registroFin = computed(() =>
    Math.min(this.currentPage() * this.pageSize(), this.totalRegistros())
  );

  irPrimeraPagina(): void {
    this.currentPage.set(1);
  }
  irUltimaPagina(): void {
    this.currentPage.set(this.totalPages());
  }
  irPaginaAnterior(): void {
    if (this.hasPrevious()) this.currentPage.update((p) => p - 1);
  }
  irPaginaSiguiente(): void {
    if (this.hasNext()) this.currentPage.update((p) => p + 1);
  }
  onPageSizeChange(nuevoTamanio: number): void {
    this.pageSize.set(nuevoTamanio);
    this.currentPage.set(1);
  }

  private recargandoDatosIniciales = false;
  private cargandoDirecciones = false;
  private cargandoTiposDireccion = false;

  readonly tableConfig = computed(() =>
    direccionesPersonaPreset.createConfig({
      data: this.direcciones(),
      pagination: {
        enabled: true,
        mode: 'client',
        pageSize: 10,
        pageSizeOptions: [5, 10, 25],
        currentPageReportTemplate:
          'Mostrando {first} a {last} de {totalRecords} direcciones'
      },
      style: {
        tableClassName: 'tabla-cambio-orientacion',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No hay direcciones registradas'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '420px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de direcciones de persona',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // Modals
  municipioSeleccionado = signal<Municipio | undefined>(undefined);
  telefonosDialogVisible = false;
  telefonosDireccion = { fijo: '', celular: '', fax: '' };

  // Formulario
  direccionForm: DireccionPersonaRequest = createEmptyDireccionRequest();
  editingId?: number;

  // Catalogos dinamicos - reemplaza constantes hardcoded
  tiposDireccion = signal<TipoDireccionDto[]>([]);
  tiposVia = signal<NomenclaturaDto[]>([]);
  apendices = signal<NomenclaturaDto[]>([]);
  cardinalidades = signal<NomenclaturaDto[]>([]);
  estratos = signal<NomenclaturaDto[]>([]);

  // Opciones para dropdowns (formato PrimeNG)
  tiposDireccionOptions = signal<any[]>([]);
  tiposViaOptions = signal<any[]>([]);
  apendicesOptions = signal<any[]>([]);
  cardinalidadesOptions = signal<any[]>([]);
  estratosOptions = computed(() =>
    this.nomenclaturaService.toDropdownOptions(this.estratos())
  );

  async ngOnInit(): Promise<void> {
    // Cargar nomenclaturas basicas (independientes de persona) con fallback
    await this.cargarNomenclaturasBase();
    await this.cargarEstratos();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.recargandoDatosIniciales) {
      return;
    }

    // Recargar cuando cambia cdpersona
    if (changes['cdpersona'] && this.cdpersona) {
      this.recargandoDatosIniciales = true;
      this.currentPage.set(1);
      try {
        await Promise.allSettled([
          this.cargarDirecciones(),
          this.cargarTiposDireccionPorPersona()
        ]);
      } finally {
        this.recargandoDatosIniciales = false;
      }
      // Recargar cuando el dialogo padre se abre sin cambio de persona
    } else if (
      changes['visible'] &&
      changes['visible'].currentValue === true &&
      this.cdpersona
    ) {
      this.recargandoDatosIniciales = true;
      this.currentPage.set(1);
      try {
        await Promise.allSettled([
          this.cargarDirecciones(),
          this.cargarTiposDireccionPorPersona()
        ]);
      } finally {
        this.recargandoDatosIniciales = false;
      }
    }
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  /**
   * IMPLEMENTADO: Carga nomenclaturas basicas (tipos via, apendices, cardinalidades)
   *
   * Reemplaza: UtilidadesBuscadores.llenarListBox() calls en IngresarDireccionFormateadaViewImpl
   * - Linea 61: TIPO VIA
   * - Linea 65: APENDICE  
   * - Linea 68: CARDINALIDAD
   *
   * MODO DESARROLLO: Usa fallbacks silenciosos si backend no esta disponible
   */
  async cargarNomenclaturasBase(): Promise<void> {
    try {
      this.loadingNomenclatura.set(true);

      // Carga paralela de nomenclaturas basicas - Performance improvement vs legacy secuencial
      const [tiposVia, apendices, cardinalidades] = await Promise.all([
        this.nomenclaturaService.listarTiposVia(),
        this.nomenclaturaService.listarApendices(),
        this.nomenclaturaService.listarCardinalidades()
      ]);

      // Actualizar signals con datos cargados
      this.tiposVia.set(tiposVia);
      this.apendices.set(apendices);
      this.cardinalidades.set(cardinalidades);

      // Convertir a formato PrimeNG dropdown
      this.tiposViaOptions.set(
        this.nomenclaturaService.toDropdownOptions(tiposVia)
      );
      this.apendicesOptions.set(
        this.nomenclaturaService.toDropdownOptions(apendices)
      );
      this.cardinalidadesOptions.set(
        this.nomenclaturaService.toDropdownOptions(cardinalidades)
      );
    } catch (error) {
      // Sin hardcoded: mantener signals vacios hasta que backend responda
      if (!environment.production) {
        console.warn(
          'Nomenclaturas no disponibles:',
          error instanceof Error ? error.message : error
        );
      }

      // Los signals quedan vacios, los dropdowns mostraran estados de "vacio"
      // Esto es mejor que hardcoded data que luego hay que mantener
    } finally {
      this.loadingNomenclatura.set(false);
    }
  }

  /**
   * IMPLEMENTADO: Carga tipos de direccion especificos por persona
   *
   * Reemplaza: AdminDireccionesActivity.listarTiposDirecciones() del legacy
   * - Linea 284 en AdminDireccionesViewImpl
   *
   * LOGICA:
   * 1. Obtiene tipo de persona (cdtipo_persona)
   * 2. Consulta TPSC_TIPO_PERSONA_DIRECC filtrado
   * 3. JOIN con TPSC_TIPOS_DIRECCIONES
   * 4. Filtra activos (snactivo = 'S')
   */
  async cargarTiposDireccionPorPersona(): Promise<void> {
    if (!this.cdpersona || this.cargandoTiposDireccion) {
      return;
    }

    this.cargandoTiposDireccion = true;

    try {
      const tipos =
        await this.nomenclaturaService.listarTiposDireccionPorPersona(
          this.cdpersona
        );

      // Actualizar signals
      this.tiposDireccion.set(tipos);

      const options = this.nomenclaturaService.toTipoDireccionDropdownOptions(
        tipos,
        false
      );

      // No incluir opcion vacia - el placeholder del dropdown ya cumple esa funcion
      this.tiposDireccionOptions.set(options);
    } catch (error) {
      // Sin hardcoded: signal vacio hasta que backend responda
      if (!environment.production) {
        console.warn(
          'Tipos direccion no disponibles para persona',
          this.cdpersona,
          error
        );
      }

      // tiposDireccion signal queda vacio [], dropdown mostrara "Sin opciones"
      // Mejor que mantener datos hardcoded
    } finally {
      this.cargandoTiposDireccion = false;
    }
  }

  /**
   * Carga estratos socioeconomicos desde base de datos
   * Reemplaza constante ESTRATOS hardcoded
   */
  async cargarEstratos(): Promise<void> {
    try {
      const estratos = await this.nomenclaturaService.listarEstratos();

      this.estratos.set(estratos);
    } catch (error) {
      if (!environment.production) {
        console.warn('Estratos no disponibles:', error);
      }
    }
  }

  async cargarDirecciones(): Promise<void> {
    if (!this.cdpersona || this.cargandoDirecciones) return;

    this.cargandoDirecciones = true;
    this.loading.set(true);
    try {
      // Solo mostrar direcciones activas (snactivo = 'S')
      const data = await this.direccionRepository.getActivasByPersona(
        this.cdpersona
      );
      this.direcciones.set(data);
      this.currentPage.set(1);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar las direcciones'
      });
    } finally {
      this.cargandoDirecciones = false;
      this.loading.set(false);
    }
  }

  getTipoLabel(codigo?: number | string): string {
    if (!codigo) return '-';
    // Buscar por consecutivo (nmtipo_persona_direccion) que es lo que guarda la direccion
    const tipos = this.tiposDireccion();
    const codigoNum =
      typeof codigo === 'string' ? parseInt(codigo, 10) : codigo;

    // Primero buscar por consecutivo (preferido para direcciones)
    const tipoPorConsecutivo = tipos.find((t) => t.consecutivo === codigoNum);
    if (tipoPorConsecutivo) {
      return tipoPorConsecutivo.descripcion;
    }

    // Fallback: buscar por codigo string
    const tipoPorCodigo = tipos.find(
      (t) => t.codigo === codigo || t.codigo === codigo.toString()
    );
    return tipoPorCodigo?.descripcion || `Tipo ${codigo}`;
  }

  verTelefonos(dir: DireccionPersona): void {
    this.telefonosDireccion = {
      fijo: dir.dstelefono_fijo || '',
      celular: dir.dstelefono_celular || '',
      fax: dir.dsfax || ''
    };
    this.telefonosDialogVisible = true;
  }

  openNew(): void {
    this.direccionForm = createEmptyDireccionRequest();
    if (this.cdpersona) {
      this.direccionForm.cdpersona = this.cdpersona;
    }
    this.editingId = undefined;
    this.editMode.set(false);
    this.dialogVisible = true;
  }

  editDireccion(dir: DireccionPersona): void {
    this.direccionForm = {
      cdpersona: dir.cdpersona || this.cdpersona!,
      nmtipo_persona_direccion: dir.nmtipo_persona_direccion!,
      cdmunicipio: dir.cdmunicipio || '',
      dsdireccion: dir.dsdireccion || '',
      dsbarrio: dir.dsbarrio,
      nmestrato: dir.nmestrato,
      dslocalidad: dir.dslocalidad,
      dstelefono_fijo: dir.dstelefono_fijo,
      dstelefono_celular: dir.dstelefono_celular,
      dsfax: dir.dsfax,
      ptvectorx: dir.ptvectorx,
      ptvectory: dir.ptvectory
    };
    this.editingId = dir.nmconsecutivo;
    this.editMode.set(true);
    this.dialogVisible = true;
  }

  hideDialog(): void {
    this.dialogVisible = false;
    this.direccionForm = createEmptyDireccionRequest();
    this.editingId = undefined;
  }

  async saveDireccion(): Promise<void> {
    // Validaciones basicas
    if (
      !this.direccionForm.nmtipo_persona_direccion ||
      !this.direccionForm.cdmunicipio ||
      !this.direccionForm.dsdireccion
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Complete los campos obligatorios'
      });
      return;
    }

    this.saving.set(true);
    try {
      if (this.editMode() && this.editingId) {
        await this.direccionRepository.update(
          this.cdpersona!,
          this.editingId,
          this.direccionForm
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Dirección actualizada correctamente'
        });
      } else {
        await this.direccionRepository.create(
          this.cdpersona!,
          this.direccionForm
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Dirección creada correctamente'
        });
      }

      this.hideDialog();
      await this.cargarDirecciones();
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.error?.message || 'No se pudo guardar la dirección'
      });
    } finally {
      this.saving.set(false);
    }
  }

  confirmDelete(dir: DireccionPersona): void {
    this.confirmationService.confirm({
      key: 'direccionConfirm',
      message: `¿Está seguro de inactivar esta dirección?`,
      header: 'Confirmar Inactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Inactivar',
      rejectLabel: 'Cancelar',
      accept: () => this.deleteDireccion(dir)
    });
  }

  async deleteDireccion(dir: DireccionPersona): Promise<void> {
    if (!dir.nmconsecutivo) return;

    this.loading.set(true);
    try {
      await this.direccionRepository.delete(this.cdpersona!, dir.nmconsecutivo);
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Dirección inactivada correctamente'
      });
      await this.cargarDirecciones();
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.error?.message || 'No se pudo inactivar la dirección'
      });
    } finally {
      this.loading.set(false);
    }
  }

  // === MUNICIPIO MODAL METHODS ===

  abrirBuscadorMunicipio(): void {
    this.buscadorMunicipio.abrir();
  }

  onMunicipioSeleccionado(municipio: Municipio): void {
    this.municipioSeleccionado.set(municipio);
    this.direccionForm.cdmunicipio = municipio.cdmunicipio;
  }

  // === FORMATEADOR DIRECCION MODAL METHODS ===

  abrirFormateadorDireccion(): void {
    this.formateadorDireccion.abrir(
      this.direccionForm.dsdireccion,
      this.municipioSeleccionado(),
      this.direccionForm.dsbarrio
    );
  }

  onDireccionFormateada(resultado: {
    direccion: string;
    municipio?: Municipio;
    barrio?: string;
  }): void {
    // Actualizar direccion formateada
    this.direccionForm.dsdireccion = resultado.direccion;

    // Actualizar municipio si se selecciono uno
    if (resultado.municipio) {
      this.municipioSeleccionado.set(resultado.municipio);
      this.direccionForm.cdmunicipio = resultado.municipio.cdmunicipio;
    }

    // Actualizar barrio si se especifico
    if (resultado.barrio) {
      this.direccionForm.dsbarrio = resultado.barrio;
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Dirección Formateada',
      detail: 'La dirección se ha formateado correctamente'
    });
  }

  // === INPUT HANDLERS - TIPO DIRECCION ===

  /** Handler para cambio de tipo de direccion - debug */
  onTipoChange(event: any): void {}

  // === INPUT HANDLERS - TELEFONOS ===

  /** Handler para keypress - solo permite numeros, guiones y espacios */
  phoneKeypress = phoneKeypress;

  /** Handler para input de telefono - sanitiza y actualiza */
  onPhoneInput(
    event: Event,
    campo: 'dstelefono_fijo' | 'dstelefono_celular' | 'dsfax'
  ): void {
    const input = event.target as HTMLInputElement;
    input.value = sanitizePhone(input.value);
    this.direccionForm[campo] = input.value;
  }
}

/**
 * ===============================================================================
 * MIGRACION LEGACY: Analisis Completo Sistema Direcciones
 * ===============================================================================
 *
 * ANALISIS COMPLETADO:
 * -----------------------
 * - Investigacion AdminDireccionesViewImpl.java - patron de carga combos
 * - Analisis IngresarDireccionFormateadaViewImpl.java - formateador legacy
 * - Identificacion UtilidadesBuscadores.llenarListBox() - metodo generico
 * ?o. Mapeo tablas fuente: TPSC_TIPOS_DIRECCIONES y TPSC_PARAM_NOMENCLATURA
 *
 * BACKEND REQUERIDO - 4 ENDPOINTS DINAMICOS:
 * ----------------------------------------------
 *
 * 1) GET /api/personas/{cdPersona}/tipos-direccion (PRIORIDAD ALTA)
 *    - Parametro: cdPersona (number)
 *    - Logica: AdminDireccionesActivity.listarTiposDirecciones()
 *    - Tabla: TPSC_TIPOS_DIRECCIONES + TPSC_TIPO_PERSONA_DIRECC
 *    - Filtro: Por cdtipo_persona, snactivo = 'S'
 *    - Response: TipoDireccionDto[] con codigos STRING (no numericos)
 *
 * 2) GET /api/nomenclatura/tipos-via (NUEVO)
 *    - Logica: UtilidadesBuscadores.llenarListBox() con parametro2 = "TIPO VIA"
 *    - Tabla: TPSC_PARAM_NOMENCLATURA
 *    - Filtro: DSTIPO_CAMPO = 'TIPO VIA'
 *    - Response: NomenclaturaDto[] (codigo, descripcion)
 *
 * 3) GET /api/nomenclatura/apendices (NUEVO)
 *    - Logica: UtilidadesBuscadores.llenarListBox() con parametro2 = "APENDICE"
 *    - Tabla: TPSC_PARAM_NOMENCLATURA
 *    - Filtro: DSTIPO_CAMPO = 'APENDICE'
 *    - Response: NomenclaturaDto[]
 *
 * 4) GET /api/nomenclatura/cardinalidades (NUEVO)
 *    - Logica: UtilidadesBuscadores.llenarListBox() con parametro2 = "CARDINALIDAD"
 *    - Tabla: TPSC_PARAM_NOMENCLATURA
 *    - Filtro: DSTIPO_CAMPO = 'CARDINALIDAD'
 *    - Response: NomenclaturaDto[]
 *
 * ESTRUCTURA TABLAS LEGACY:
 * ----------------------------
 * TPSC_TIPOS_DIRECCIONES:
 * - cdtipo_direccion: VARCHAR(PK) - 'CORR', 'DOMI', 'FAMI', etc.
 * - dstipo_direccion: VARCHAR - 'Correspondencia', 'Domicilio', etc.
 *
 * TPSC_TIPO_PERSONA_DIRECC (relacion):
 * - nmconsecutivo: NUMBER(PK)
 * - cdtipo_direccion: VARCHAR(FK)
 * - cdtipo_persona: VARCHAR(FK) - filtro clave
 *
 * TPSC_PARAM_NOMENCLATURA (nuevos listados):
 * - DSTIPO_CAMPO: VARCHAR - categoria ('TIPO VIA', 'APENDICE', 'CARDINALIDAD')
 * - DSNOMBRE_CAMPO: VARCHAR - codigo (ej: 'CL', 'CR', 'A', 'SUR', etc.)
 * - [descripcion]: VARCHAR - descripcion completa
 *
 * FRONTEND REQUERIDO - SERVICIOS:
 * ----------------------------------
 *
 * 1) TiposNomenclaturaService (NUEVO):
 *    ```typescript
 *    async listarTiposVia(): Promise<NomenclaturaDto[]>
 *    async listarApendices(): Promise<NomenclaturaDto[]>
 *    async listarCardinalidades(): Promise<NomenclaturaDto[]>
 *    ```
 *
 * 2) TiposDireccionService (ACTUALIZAR):
 *    ```typescript
 *    async listarPorPersona(cdPersona: number): Promise<TipoDireccionDto[]>
 *    ```
 *
 * ORDEN DE CARGA LEGACY (AdminDireccionesViewImpl patron):
 * -----------------------------------------------------------
 * 1. Constructor: Cargar tipos via, apendices, cardinalidades (estaticos por tabla)
 * 2. onRowLinkClickEvent: Cargar tipos direccion (dinamico por persona)
 * 3. editarDireccion: Abrir IngresarDireccionFormateadaActivity
 *
 * CAMBIOS FRONTEND REQUERIDOS:
 * -------------------------------
 *
 * DireccionesPersonaComponent:
 * - Remover: TIPOS_DIRECCION_LEGACY_EJEMPLO (hardcoded)
 * - Remover: TIPOS_VIA, APENDICES, CARDINALIDADES (hardcoded)
 * - Agregar: cargarListadosNomenclatura() en ngOnInit()
 * - Agregar: cargarTiposDireccion() por cdPersona
 * - Agregar: loading states para todos los combos
 *
 * FormateadorDireccionComponent:
 * - Remover: constantes hardcoded TIPOS_VIA, APENDICES, CARDINALIDADES
 * - Recibir: datos por @Input() desde componente padre
 * - Cargar: via servicio TiposNomenclaturaService
 *
 * VALIDACIONES CRITICAS:
 * -------------------------
 * - TIPOS_DIRECCION: codigos STRING (no numeros) segun TPSC_TIPOS_DIRECCIONES
 * - NOMENCLATURA: Cargar desde DB, no constantes hardcoded
 * - FILTROS: Tipos direccion dependen de cdtipo_persona
 * - ORDEN: Cargar nomenclatura al inicio, tipos direccion por persona
 *
 * PROGRESS TRACKING:
 * ---------------------
 * - FASE 1: Analisis legacy completado
 * - FASE 2: Patron de carga identificado
 * ?o. FASE 3: Estructura tablas mapeada
 * - FASE 4: Implementar backend endpoints
 * - FASE 5: Actualizar servicios frontend
 * - FASE 6: Integrar carga dinamica
 * ? FASE 7: Testing legacy compliance
 *
 * PROXIMO PASO: NomenclaturaService backend COMPLETADO - Integrar en componentes
 *
 * MIGRACION COMPLETADA:
 * ------------------------
 * - UtilidadesBuscadores.java -> NomenclaturaService (Angular)
 * - SuraCommand "recuperarDatosLista" -> NomenclaturaController (Spring Boot)
 * - GenericoLista/BusquedaGeneral -> DTOs modernos
 * - Direct JDBC -> R2DBC Oracle reactive
 * - GWT ListBox population -> PrimeNG dropdown integration ready
 *
 * ARCHIVOS CREADOS:
 * -------------------
 * Frontend:
 * - angular/src/app/domain/shared/models/nomenclatura.ts
 * - angular/src/app/infrastructure/shared/nomenclatura.service.ts
 *
 * Backend:
 * - domain/dto/NomenclaturaDto.java (con BusquedaRequest/Response)
 * - domain/usecase/NomenclaturaUseCase.java (logica de negocio)
 * - domain/gateway/NomenclaturaRepository.java (interface)
 * - infrastructure/NomenclaturaR2dbcRepository.java (R2DBC implementation)
 * - web/NomenclaturaController.java (REST endpoints)
 *
 * ENDPOINTS DISPONIBLES:
 * -------------------------
 * GET /api/nomenclatura/buscar (generico - reemplaza SuraCommand)
 * GET /api/nomenclatura/tipos-via (optimizado)
 * GET /api/nomenclatura/apendices (optimizado)  
 * GET /api/nomenclatura/cardinalidades (optimizado)
 * GET /api/nomenclatura/tipos-direccion/{cdPersona} (con logica persona)
 *
 * PATRON DE USO EN COMPONENTES:
 * -------------------------------
 * ```typescript
 * // Reemplaza UtilidadesBuscadores.llenarListBox()
 * const tiposVia = await this.nomenclaturaService.listarTiposVia();
 * this.tiposViaOptions = this.nomenclaturaService.toDropdownOptions(tiposVia);
 * ```
 * ===============================================================================
 */
